import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send, 
  MessageSquare,
  Calendar,
  Building2,
  ArrowRight,
  Menu,
  X,
  CheckCircle2,
  Loader2,
  AlertCircle
} from "lucide-react";

const SPEAKR_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/8ueg8ogv_Futuristic%20Speakr%20AI%20logo%20design.png";
const API_URL = process.env.REACT_APP_API_URL;

export default function Contact() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    type: "general"
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const navLinks = [
    { name: "Features", href: "/#features" },
    { name: "Industries", href: "/#industries" },
    { name: "Pricing", href: "/pricing" },
    { name: "Resources", href: "/resources" },
    { name: "Contact", href: "/contact" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/contact/submit`, {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        phone: formData.phone || null,
        message: formData.message,
        inquiry_type: formData.type
      });
      
      if (response.data.success) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error("Contact form error:", err);
      setError(err.response?.data?.detail || "Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email Us",
      description: "For general inquiries and support",
      value: "info@gskproductions.ca",
      action: "mailto:info@gskproductions.ca"
    },
    {
      icon: Phone,
      title: "Call Us",
      description: "Speak with our team directly",
      value: "+1 (647) 861-3970",
      action: "tel:+16478613970"
    },
    {
      icon: Calendar,
      title: "Book a Demo",
      description: "See Speakr in action",
      value: "Schedule a call",
      action: "/register?demo=true"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800/50 backdrop-blur-sm sticky top-0 z-50 bg-slate-950/80">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
              <img src={SPEAKR_LOGO} alt="Speakr" className="h-10 w-auto" />
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-medium transition-colors ${
                    link.href === "/contact" 
                      ? "text-white" 
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <Button variant="ghost" className="text-slate-300 hover:text-white" onClick={() => navigate("/login")}>
                Sign In
              </Button>
              <Button onClick={() => navigate("/register")} className="bg-gradient-to-r from-blue-600 to-purple-600">
                Get Started
              </Button>
            </div>

            <button
              className="md:hidden p-2 text-slate-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-slate-800 pt-4">
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-slate-300 hover:text-white py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="flex flex-col gap-2 pt-4 border-t border-slate-800">
                  <Button variant="ghost" className="justify-start text-slate-300" onClick={() => navigate("/login")}>
                    Sign In
                  </Button>
                  <Button onClick={() => navigate("/register")} className="bg-gradient-to-r from-blue-600 to-purple-600">
                    Get Started
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-blue-500/10 text-blue-400 border-blue-500/20">
            <MessageSquare className="w-3 h-3 mr-1" />
            Get in Touch
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            We'd Love to Hear From You
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Have questions about Speakr? Want a personalized demo? Our team is here to help you transform your business communications.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactMethods.map((method, idx) => {
              const Icon = method.icon;
              return (
                <a
                  key={idx}
                  href={method.action.startsWith("/") ? undefined : method.action}
                  onClick={method.action.startsWith("/") ? () => navigate(method.action) : undefined}
                  className="block"
                >
                  <Card className="bg-slate-800/50 border-slate-700/50 hover:border-blue-500/50 transition-all cursor-pointer h-full">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-6 h-6 text-blue-400" />
                      </div>
                      <h3 className="font-semibold text-white mb-1">{method.title}</h3>
                      <p className="text-sm text-slate-400 mb-3">{method.description}</p>
                      <p className="text-blue-400 font-medium">{method.value}</p>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <Card className="bg-slate-800/30 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white">Send Us a Message</CardTitle>
                <CardDescription className="text-slate-400">
                  Fill out the form below and we'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Message Sent!</h3>
                    <p className="text-slate-400 mb-2">
                      Thanks for reaching out. Our team will get back to you within 24 hours.
                    </p>
                    <p className="text-sm text-slate-500 mb-6">
                      Check your email for a confirmation message.
                    </p>
                    <Button 
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: "", email: "", company: "", phone: "", message: "", type: "general" });
                        setError(null);
                      }} 
                      variant="outline" 
                      className="border-slate-600"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-300">Name *</Label>
                        <Input
                          id="name"
                          required
                          placeholder="John Smith"
                          className="bg-slate-900/50 border-slate-700 text-white"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          placeholder="john@company.com"
                          className="bg-slate-900/50 border-slate-700 text-white"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-slate-300">Company</Label>
                        <Input
                          id="company"
                          placeholder="Your Company"
                          className="bg-slate-900/50 border-slate-700 text-white"
                          value={formData.company}
                          onChange={(e) => setFormData({...formData, company: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-300">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+1 (647) 861-3970"
                          className="bg-slate-900/50 border-slate-700 text-white"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="type" className="text-slate-300">Inquiry Type</Label>
                      <select
                        id="type"
                        className="w-full h-10 px-3 rounded-md bg-slate-900/50 border border-slate-700 text-white"
                        value={formData.type}
                        onChange={(e) => setFormData({...formData, type: e.target.value})}
                      >
                        <option value="general">General Inquiry</option>
                        <option value="demo">Request a Demo</option>
                        <option value="sales">Sales Question</option>
                        <option value="support">Technical Support</option>
                        <option value="partnership">Partnership</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300">Message *</Label>
                      <textarea
                        id="message"
                        required
                        rows={4}
                        placeholder="Tell us how we can help..."
                        className="w-full px-3 py-2 rounded-md bg-slate-900/50 border border-slate-700 text-white resize-none"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                      />
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Company Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Our Office</h3>
                <Card className="bg-slate-800/30 border-slate-700/50">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-blue-400 mt-0.5" />
                      <div>
                        <p className="text-white font-medium">GSK Productions Inc.</p>
                        <p className="text-slate-400">Toronto, Ontario, Canada</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-emerald-400 mt-0.5" />
                      <div>
                        <p className="text-white font-medium">Business Hours</p>
                        <p className="text-slate-400">Mon - Fri: 9:00 AM - 6:00 PM EST</p>
                        <p className="text-slate-400">Sat - Sun: Closed</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-purple-400 mt-0.5" />
                      <div>
                        <p className="text-white font-medium">Coverage</p>
                        <p className="text-slate-400">Serving businesses across Canada & USA</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Quick Links</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "View Pricing", href: "/pricing" },
                    { label: "Start Free Trial", href: "/register" },
                    { label: "Browse Industries", href: "/#industries" },
                    { label: "Read FAQ", href: "/pricing#faq" }
                  ].map((link, idx) => (
                    <Link
                      key={idx}
                      to={link.href}
                      className="flex items-center gap-2 p-3 bg-slate-800/30 border border-slate-700/50 rounded-lg text-slate-300 hover:text-white hover:border-blue-500/50 transition-all"
                    >
                      <ArrowRight className="w-4 h-4" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              <Card className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-500/20">
                <CardContent className="p-6 text-center">
                  <Calendar className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-2">Prefer a Live Demo?</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Schedule a personalized walkthrough with our team
                  </p>
                  <Button 
                    onClick={() => navigate("/register?demo=true")}
                    className="bg-gradient-to-r from-blue-600 to-purple-600"
                  >
                    Book a Demo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={SPEAKR_LOGO} alt="Speakr" className="h-8 w-auto" />
          </Link>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Speakr™ by GSK Productions Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
